/**
 * A tela Permissões (história 11.1) — a matriz **por perfil**, e os perfis personalizados.
 *
 * Mora, até a 11.2, como aba da Administração; a 11.2 a leva para `/configuracao` sem reescrita.
 *
 * O que ela desenha vem pronto do serviço (`GET /perfis`): os quatro de fábrica e os
 * personalizados, cada um com o escopo de carteira, a contagem de usuários e o mapa agrupado nas
 * oito áreas do FR-61-A, linha a linha com rótulo, estado, a marca ✳ (`limitada`, derivada de
 * concedida + escopo `propria`), os `estados_disponiveis` e o `motivo_do_teto`. O SPA **não**
 * guarda cópia do catálogo, e não decide teto nenhum: a linha que só aceita `negada` vem
 * desabilitada com a frase do serviço.
 *
 * **O mapa dos de fábrica é editável** (o FR-61-A é a semente, AD-18); o nome e o escopo, não. O
 * personalizado nasce como cópia do perfil de fábrica escolhido e com o escopo escolhido — o
 * escopo nunca muda depois. Renomear e excluir existem só para personalizado; a recusa do serviço
 * (nome repetido, perfil com usuários) aparece como veio.
 *
 * **Salvar manda o mapa completo** (`PUT /perfis/{id}/permissoes`, as 40 desde a 13.11b): o rascunho parte do
 * que foi lido e troca só as linhas mexidas. Vale para todos os usuários do perfil na próxima
 * renovação do acesso (até 15 minutos).
 *
 * Só o admin de fábrica vê a tela (`ehAdminDeFabrica`); para os demais o componente devolve
 * `null` sem disparar consulta. Quem autoriza é o serviço.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Dialogo } from '@/modulos/negocios/Dialogo'
import {
  CHAVE_DOS_PERFIS,
  type EscopoDeCarteira,
  type MapaDePermissoes,
  type Perfil,
  type PerfilDoCatalogo,
  criarPerfil,
  definirPermissoesDoPerfil,
  ehAdminDeFabrica,
  excluirPerfil,
  mapaDoPerfil,
  renomearPerfil,
  usePerfis,
} from './api'
import { useSessao } from './sessao'
import './gestao-de-acesso.css'

const ID_DO_TITULO = 'titulo-das-permissoes'

const DESCRICAO_DO_ESCOPO: Record<EscopoDeCarteira, string> = {
  todas: 'Enxerga todas as carteiras',
  propria: 'Enxerga só a própria carteira',
}

const ORDEM_DOS_BASES: Perfil[] = ['admin', 'gestor_comercial', 'vendedor_closer', 'sdr']

type DialogoAberto = 'novo' | 'renomear' | 'excluir' | null

function nomeDoBase(perfis: PerfilDoCatalogo[], base: Perfil): string {
  return perfis.find((perfil) => perfil.de_fabrica && perfil.perfil_base === base)?.nome ?? base
}

function NovoPerfil({
  perfis,
  aoCriar,
  aoFechar,
}: {
  perfis: PerfilDoCatalogo[]
  aoCriar: (perfil: PerfilDoCatalogo) => void
  aoFechar: () => void
}) {
  const clienteDeConsultas = useQueryClient()
  const [nome, setNome] = useState('')
  const [base, setBase] = useState<Perfil>('vendedor_closer')
  const [escopo, setEscopo] = useState<EscopoDeCarteira>('propria')
  const criacao = useMutation({
    mutationFn: () => criarPerfil({ nome, perfil_base: base, escopo_de_carteira: escopo }),
    onSuccess: async (criado) => {
      await clienteDeConsultas.invalidateQueries({
        queryKey: CHAVE_DOS_PERFIS,
      })
      aoCriar(criado)
    },
  })

  return (
    <Dialogo
      titulo="Novo perfil personalizado"
      subtitulo="Nasce como cópia das permissões do perfil escolhido; você ajusta depois."
      rotuloDoConfirmar="Criar perfil"
      confirmarDesabilitado={nome.trim() === ''}
      ocupado={criacao.isPending}
      erro={criacao.isError ? criacao.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        criacao.mutate()
      }}
    >
      <label className="acesso__campo">
        <span className="acesso__rotulo">Nome</span>
        <input
          className="acesso__entrada"
          value={nome}
          maxLength={80}
          placeholder="ex.: Vendedor sênior, Pós-venda"
          onChange={(evento) => {
            setNome(evento.target.value)
          }}
        />
      </label>
      <label className="acesso__campo">
        <span className="acesso__rotulo">Copiar de</span>
        <select
          className="acesso__entrada"
          value={base}
          onChange={(evento) => {
            setBase(evento.target.value as Perfil)
          }}
        >
          {ORDEM_DOS_BASES.map((opcao) => (
            <option key={opcao} value={opcao}>
              {nomeDoBase(perfis, opcao)}
            </option>
          ))}
        </select>
      </label>
      <label className="acesso__campo">
        <span className="acesso__rotulo">O que enxerga</span>
        <select
          className="acesso__entrada"
          value={escopo}
          onChange={(evento) => {
            setEscopo(evento.target.value as EscopoDeCarteira)
          }}
        >
          <option value="propria">Só a própria carteira</option>
          <option value="todas">Todas as carteiras</option>
        </select>
      </label>
      <p className="acesso__aviso">
        O escopo de carteira não muda depois de criado. Custo, margem e o valor para quem deriva da
        SDR continuam fora, sempre.
      </p>
    </Dialogo>
  )
}

function Renomear({ perfil, aoFechar }: { perfil: PerfilDoCatalogo; aoFechar: () => void }) {
  const clienteDeConsultas = useQueryClient()
  const [nome, setNome] = useState(perfil.nome)
  const renomeacao = useMutation({
    mutationFn: () => renomearPerfil(perfil.id, nome),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({
        queryKey: CHAVE_DOS_PERFIS,
      })
      aoFechar()
    },
  })
  return (
    <Dialogo
      titulo="Renomear perfil"
      subtitulo={perfil.nome}
      rotuloDoConfirmar="Salvar"
      confirmarDesabilitado={nome.trim() === '' || nome === perfil.nome}
      ocupado={renomeacao.isPending}
      erro={renomeacao.isError ? renomeacao.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        renomeacao.mutate()
      }}
    >
      <label className="acesso__campo">
        <span className="acesso__rotulo">Nome</span>
        <input
          className="acesso__entrada"
          value={nome}
          maxLength={80}
          onChange={(evento) => {
            setNome(evento.target.value)
          }}
        />
      </label>
    </Dialogo>
  )
}

function Excluir({
  perfil,
  aoExcluir,
  aoFechar,
}: {
  perfil: PerfilDoCatalogo
  aoExcluir: () => void
  aoFechar: () => void
}) {
  const clienteDeConsultas = useQueryClient()
  const exclusao = useMutation({
    mutationFn: () => excluirPerfil(perfil.id),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({
        queryKey: CHAVE_DOS_PERFIS,
      })
      aoExcluir()
    },
  })
  return (
    <Dialogo
      titulo="Excluir perfil"
      subtitulo={perfil.nome}
      rotuloDoConfirmar="Excluir"
      ocupado={exclusao.isPending}
      erro={exclusao.isError ? exclusao.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        exclusao.mutate()
      }}
    >
      <p className="acesso__nota">
        Só é possível excluir um perfil sem usuários. Os perfis de fábrica não são excluídos.
      </p>
    </Dialogo>
  )
}

/** A matriz de um perfil, com o rascunho das linhas mexidas. */
function Matriz({ perfil }: { perfil: PerfilDoCatalogo }) {
  const clienteDeConsultas = useQueryClient()
  const [rascunho, setRascunho] = useState<MapaDePermissoes>(() => mapaDoPerfil(perfil))
  const [salvo, setSalvo] = useState(false)
  const gravacao = useMutation({
    mutationFn: () => definirPermissoesDoPerfil(perfil.id, rascunho),
    onSuccess: async () => {
      setSalvo(true)
      await clienteDeConsultas.invalidateQueries({
        queryKey: CHAVE_DOS_PERFIS,
      })
    },
  })
  const original = mapaDoPerfil(perfil)
  const mexido = Object.entries(rascunho).some(
    ([permissao, estado]) => original[permissao as keyof MapaDePermissoes] !== estado,
  )
  const concedidas = Object.values(rascunho).filter((estado) => estado === 'concedida').length
  const total = Object.keys(rascunho).length

  return (
    <div className="acesso__corpo">
      <p className="acesso__nota">
        <span className="numero">{concedidas}</span> de <span className="numero">{total}</span>{' '}
        permissões concedidas · ✳ concedida, limitada à própria carteira
      </p>
      {perfil.areas.map((area) => (
        <div key={area.area} role="group" aria-label={area.rotulo}>
          <p className="acesso__grupo">{area.rotulo}</p>
          {area.linhas.map((linha) => {
            const estado = rascunho[linha.permissao]
            const ligada = estado === 'concedida'
            // Travada nos dois sentidos: acima do teto (só `negada`) ou, no Admin de fábrica, as
            // três de gestão de acesso e trilha (só `concedida`, DW-307). O motivo vem do backend.
            const editavel = linha.estados_disponiveis.length > 1
            return (
              <div key={linha.permissao} className="acesso__permissao">
                <span className="acesso__permissao-texto">
                  <span>
                    {linha.rotulo}
                    {/* Derivado do **rascunho** (AD-18): o ✳ acompanha o interruptor antes de salvar. */}
                    {ligada &&
                    perfil.escopo_de_carteira === 'propria' &&
                    linha.recortada_pelo_escopo ? (
                      <span
                        className="acesso__marca"
                        title="Concedida, limitada à própria carteira"
                      >
                        ✳
                      </span>
                    ) : null}
                  </span>
                  {linha.motivo_do_teto === null ? null : (
                    <span className="acesso__motivo">{linha.motivo_do_teto}</span>
                  )}
                </span>
                <span className="acesso__dica">{ligada ? 'permitido' : 'negado'}</span>
                <button
                  type="button"
                  role="switch"
                  className="acesso__interruptor"
                  aria-checked={ligada}
                  aria-label={linha.rotulo}
                  disabled={!editavel || gravacao.isPending}
                  onClick={() => {
                    setSalvo(false)
                    setRascunho((atual) => ({
                      ...atual,
                      [linha.permissao]: ligada ? 'negada' : 'concedida',
                    }))
                  }}
                />
              </div>
            )
          })}
        </div>
      ))}
      {gravacao.isError ? (
        <p className="acesso__erro" role="alert">
          {gravacao.error.message}
        </p>
      ) : null}
      {salvo && !mexido ? (
        <p className="acesso__sucesso" role="status">
          Permissões salvas. Valem para as pessoas deste perfil na próxima renovação do acesso (até
          15 minutos).
        </p>
      ) : null}
      <div className="acesso__acoes">
        <button
          type="button"
          className="acesso__botao acesso__botao--discreto"
          disabled={!mexido || gravacao.isPending}
          onClick={() => {
            setRascunho(mapaDoPerfil(perfil))
            gravacao.reset()
          }}
        >
          Descartar
        </button>
        <button
          type="button"
          className="acesso__botao"
          disabled={!mexido || gravacao.isPending}
          onClick={() => {
            gravacao.mutate()
          }}
        >
          Salvar permissões
        </button>
      </div>
    </div>
  )
}

export function Permissoes() {
  const { data: sessao } = useSessao()
  const podeVer = sessao !== undefined && ehAdminDeFabrica(sessao)
  const consulta = usePerfis({ habilitada: podeVer })
  const [escolhidoId, setEscolhidoId] = useState<string | null>(null)
  const [dialogo, setDialogo] = useState<DialogoAberto>(null)

  if (!podeVer) {
    return null
  }

  const perfis = consulta.data ?? []
  const escolhido = perfis.find((perfil) => perfil.id === escolhidoId) ?? perfis[0]
  const fechar = () => {
    setDialogo(null)
  }

  return (
    <section className="acesso" aria-labelledby={ID_DO_TITULO}>
      <div className="acesso__cartao">
        <div className="acesso__cabecalho">
          <div className="acesso__cabecalho-texto">
            <h2 className="acesso__titulo" id={ID_DO_TITULO}>
              Perfis e permissões
            </h2>
            <span className="acesso__nota">
              Cada pessoa tem um perfil. Os quatro de fábrica não são excluídos; o Admin cria perfis
              personalizados por cópia de um deles. Cada permissão é permitida ou negada, e o que o
              perfil enxerga (a própria carteira ou todas) é propriedade do perfil.
            </span>
          </div>
        </div>
        <div className="acesso__corpo">
          {consulta.isError ? (
            <p className="acesso__erro" role="alert">
              {consulta.error.message}{' '}
              <button
                type="button"
                className="acesso__botao acesso__botao--discreto"
                onClick={() => {
                  void consulta.refetch()
                }}
              >
                Tentar de novo
              </button>
            </p>
          ) : consulta.data === undefined ? (
            <p className="acesso__nota" role="status">
              Carregando perfis…
            </p>
          ) : (
            <div className="acesso__perfis">
              {perfis.map((perfil) => (
                <button
                  key={perfil.id}
                  type="button"
                  className="acesso__perfil"
                  aria-pressed={perfil.id === escolhido?.id}
                  onClick={() => {
                    setEscolhidoId(perfil.id)
                  }}
                >
                  <span className="acesso__perfil-nome">
                    {perfil.nome}
                    {perfil.de_fabrica ? null : <span className="acesso__chip">personalizado</span>}
                  </span>
                  <span className="acesso__perfil-detalhe">
                    {DESCRICAO_DO_ESCOPO[perfil.escopo_de_carteira]}
                  </span>
                  {perfil.de_fabrica ? null : (
                    <span className="acesso__perfil-detalhe">
                      Copiado de: {nomeDoBase(perfis, perfil.perfil_base)}
                    </span>
                  )}
                  <span className="acesso__perfil-contagem">
                    {perfil.quantidade_de_usuarios} pessoa(s)
                  </span>
                </button>
              ))}
              <button
                type="button"
                className="acesso__perfil acesso__perfil--novo"
                onClick={() => {
                  setDialogo('novo')
                }}
              >
                + Perfil personalizado
              </button>
            </div>
          )}
        </div>
      </div>

      {escolhido === undefined ? null : (
        <div className="acesso__cartao">
          <div className="acesso__cabecalho">
            <div className="acesso__cabecalho-texto">
              <h3 className="acesso__titulo">{escolhido.nome}</h3>
              <span className="acesso__nota">
                {DESCRICAO_DO_ESCOPO[escolhido.escopo_de_carteira]} · o escopo não muda
                {escolhido.de_fabrica ? ' · perfil de fábrica: nome fixo, não é excluído' : ''}
              </span>
            </div>
            {escolhido.de_fabrica ? null : (
              <div className="acesso__acoes">
                <button
                  type="button"
                  className="acesso__botao acesso__botao--discreto"
                  onClick={() => {
                    setDialogo('renomear')
                  }}
                >
                  Renomear
                </button>
                <button
                  type="button"
                  className="acesso__botao acesso__botao--perigo"
                  onClick={() => {
                    setDialogo('excluir')
                  }}
                >
                  Excluir
                </button>
              </div>
            )}
          </div>
          {/* A `key` re-semeia o rascunho quando o perfil escolhido muda. */}
          <Matriz key={escolhido.id} perfil={escolhido} />
          <div className="acesso__rodape">
            Piso duro, que nenhum perfil destrava: custo e margem nunca chegam à tela, carteira
            alheia não aparece para perfil de carteira própria, e valor nunca sai para quem deriva
            da SDR.
          </div>
        </div>
      )}

      {dialogo === 'novo' ? (
        <NovoPerfil
          perfis={perfis}
          aoCriar={(criado) => {
            setEscolhidoId(criado.id)
            fechar()
          }}
          aoFechar={fechar}
        />
      ) : null}
      {dialogo === 'renomear' && escolhido !== undefined ? (
        <Renomear perfil={escolhido} aoFechar={fechar} />
      ) : null}
      {dialogo === 'excluir' && escolhido !== undefined ? (
        <Excluir
          perfil={escolhido}
          aoExcluir={() => {
            setEscolhidoId(null)
            fechar()
          }}
          aoFechar={fechar}
        />
      ) : null}
    </section>
  )
}
